import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useStateConfigQuery } from '@/entities/state/model'
import { useRealtimeConnectionStatus, useRealtimeEnabled } from '@/features/realtime/lib'
import { formatSecondsToMinutes } from '@/shared/lib/dayjs'
import { fetchOrders, getWaitTime } from '../api/order.api'
import { transformAndGroupDashboardOrdersByStatus } from '../lib/order.group'
import { useEffectiveOrders } from '../lib/order.hooks'
import { groupPartnerOrders } from '../lib/order.partner-grouping'
import { orderKeys } from './order.queryKeys'

/**
 * Determines whether polling should be disabled based on realtime connection status.
 * Returns true when realtime is enabled AND connected, allowing the app to rely on
 * WebSocket updates instead of HTTP polling.
 *
 * Polling fallback: If realtime disconnects, polling automatically resumes to ensure
 * users always receive order updates, even if WebSocket fails.
 */
function useShouldDisablePolling(): boolean {
  const isRealtimeEnabled = useRealtimeEnabled()
  const isRealtimeConnected = useRealtimeConnectionStatus()

  return isRealtimeEnabled && isRealtimeConnected
}

interface OrdersOptions {
  disablePolling?: boolean
}

/**
 * Query options for fetching orders
 */
export function ordersQueryOptions({
  disablePolling = false,
}: OrdersOptions = {}) {
  return queryOptions({
    queryKey: orderKeys.orders(),
    queryFn: fetchOrders,
    refetchInterval: disablePolling ? false : 5000,
    refetchIntervalInBackground: true,
  })
}

/**
 * Fetches and returns orders
 */
export function useRawOrdersQuery() {
  const shouldDisablePolling = useShouldDisablePolling()

  const polledQuery = useSuspenseQuery(ordersQueryOptions({
    disablePolling: shouldDisablePolling,
  }))

  const effectiveOrders = useEffectiveOrders(polledQuery.data, polledQuery.dataUpdatedAt)

  return {
    ...polledQuery,
    data: effectiveOrders,
  }
}

/**
 * A query to get orders sorted by status with grouping and realtime integration
 * TODO: Add order sorting (by priority, date, etc.)
 * returns {Record<string, DashboardOrder[]>} - Orders grouped by status
 */
export function useDashboardOrdersQuery({
  disablePolling = false,
}: OrdersOptions = {}) {
  const shouldDisablePolling = useShouldDisablePolling()
  const { data: stateConfig } = useStateConfigQuery()

  const polledQuery = useSuspenseQuery(ordersQueryOptions({
    disablePolling: disablePolling || shouldDisablePolling,
  }))

  const effectiveOrders = useEffectiveOrders(polledQuery.data, polledQuery.dataUpdatedAt)

  const processedOrders = useMemo(() => {
    // Check if partner order grouping is enabled
    if (stateConfig?.features?.orderGrouping?.enabled) {
      const groupedOrders = groupPartnerOrders(effectiveOrders, {
        maxGroupSize: 2,
      })
      return transformAndGroupDashboardOrdersByStatus(groupedOrders)
    } else {
      // If grouping is disabled, treat all orders as individual orders
      const individualOrders = effectiveOrders.map(order => ({
        type: 'single' as const,
        id: order.orderId,
        data: order,
      }))
      return transformAndGroupDashboardOrdersByStatus(individualOrders)
    }
  }, [effectiveOrders, stateConfig?.features?.orderGrouping?.enabled])

  return {
    ...polledQuery,
    data: processedOrders,
  }
}

/**
 * Fetches and manages wait time data using TanStack Query.
 * Returns formatted wait times in minutes:seconds format
 * Wait time data is always fetched via polling, and refreshed when orders with waitTime are received
 */
export function useWaitTimeQuery({
  disablePolling = false,
}: { disablePolling?: boolean } = {}) {
  const shouldDisablePolling = useShouldDisablePolling()

  return useQuery<{
    waitTimeAverage: number
    numberOfOrders: number
    formattedWaitTime: string
  }>({
    queryKey: orderKeys.waitTime(),
    queryFn: async () => {
      const timezoneOffset = new Date().getTimezoneOffset()
      const { waitTimeAverage, numberOfOrders } = await getWaitTime(timezoneOffset)
      const formattedWaitTime = formatSecondsToMinutes(waitTimeAverage)

      return {
        waitTimeAverage,
        numberOfOrders,
        formattedWaitTime,
      }
    },
    refetchInterval: (disablePolling || shouldDisablePolling) ? false : 30000,
    refetchIntervalInBackground: true,
  })
}
